import { Button, Empty, Input, InputNumber, Table } from '@arco-design/web-react'
import { IconDelete, IconPlus } from '@arco-design/web-react/icon'
import { createRatioRowId } from '../ratioSettingUtils'

export interface RateLimitGroupRow {
  id: string
  group: string
  total: number
  success: number
}

export function parseRateLimitGroup(raw: string): RateLimitGroupRow[] {
  if (!raw?.trim()) return []
  try {
    const parsed = JSON.parse(raw) as unknown
    if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return []
    return Object.entries(parsed as Record<string, unknown>)
      .map(([group, val]) => {
        const arr = Array.isArray(val) ? val : []
        const total = Number(arr[0])
        const success = Number(arr[1])
        return {
          id: createRatioRowId('rl-group'),
          group,
          total: Number.isFinite(total) ? total : 0,
          success: Number.isFinite(success) ? success : 1,
        }
      })
      .sort((a, b) => a.group.localeCompare(b.group))
  } catch {
    return []
  }
}

export function rateLimitGroupToJson(rows: RateLimitGroupRow[]): string {
  const obj: Record<string, [number, number]> = {}
  rows.forEach((r) => {
    const g = r.group.trim()
    if (!g) return
    obj[g] = [Math.max(0, Math.floor(r.total)), Math.max(1, Math.floor(r.success))]
  })
  return JSON.stringify(obj, null, 2)
}

export function validateRateLimitGroupRows(rows: RateLimitGroupRow[]): string | null {
  const seen = new Set<string>()
  for (const row of rows) {
    const g = row.group.trim()
    if (!g) return '分组名不能为空'
    if (seen.has(g)) return `分组重复：${g}`
    seen.add(g)
    if (!Number.isFinite(row.total) || row.total < 0) return `分组「${g}」总请求数须 ≥ 0`
    if (!Number.isFinite(row.success) || row.success < 1) return `分组「${g}」成功次数须 ≥ 1`
  }
  return null
}

interface Props {
  value: string
  onChange: (json: string) => void
}

export default function RateLimitGroupEditor({ value, onChange }: Props) {
  const rows = parseRateLimitGroup(value)

  const update = (next: RateLimitGroupRow[]) => onChange(rateLimitGroupToJson(next))

  const columns = [
    {
      title: '分组名',
      render: (_: unknown, _row: RateLimitGroupRow, index: number) => (
        <Input
          value={rows[index]?.group ?? ''}
          placeholder="default / vip"
          onChange={(v) => {
            const next = [...rows]
            next[index] = { ...next[index], group: v }
            update(next)
          }}
        />
      ),
    },
    {
      title: '每周期总请求数',
      width: 160,
      render: (_: unknown, _row: RateLimitGroupRow, index: number) => (
        <InputNumber
          min={0}
          style={{ width: '100%' }}
          value={rows[index]?.total}
          onChange={(v) => {
            const next = [...rows]
            next[index] = { ...next[index], total: v ?? 0 }
            update(next)
          }}
        />
      ),
    },
    {
      title: '每周期成功次数',
      width: 160,
      render: (_: unknown, _row: RateLimitGroupRow, index: number) => (
        <InputNumber
          min={1}
          style={{ width: '100%' }}
          value={rows[index]?.success}
          onChange={(v) => {
            const next = [...rows]
            next[index] = { ...next[index], success: v ?? 1 }
            update(next)
          }}
        />
      ),
    },
    {
      title: '',
      width: 56,
      render: (_: unknown, _row: RateLimitGroupRow, index: number) => (
        <Button
          type="text"
          status="danger"
          icon={<IconDelete />}
          onClick={() => update(rows.filter((_, i) => i !== index))}
        />
      ),
    },
  ]

  return (
    <div>
      <div className="settings-table-scroll">
        <Table
          columns={columns}
          data={rows}
          pagination={false}
          rowKey="id"
          noDataElement={<Empty description="暂无分组覆盖；将使用全局阈值" />}
        />
      </div>
      <Button
        type="dashed"
        className="mt-2"
        icon={<IconPlus />}
        onClick={() =>
          update([
            ...rows,
            { id: createRatioRowId('rl-group'), group: '', total: 0, success: 1000 },
          ])
        }
      >
        添加分组覆盖
      </Button>
    </div>
  )
}
